import { useCallback, useMemo } from 'react';
import type { NavigateOptions, To } from 'react-router';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { validate as isValidUUID } from 'uuid';
import { useYwuApp } from '../contexts/ywu-app-context';

/**
 * Get the business relationship ID from the current Location (URL) and a custom navigate function which already applies the basePath.
 * For the navigateWithBase helper function:
 * - The function prefixes the given to-path with the correct base-path. Meaning either with or without the BR-UUID.
 * - Make sure the to-path is prefixed with a `/`
 * - The function accepts the same parameters as the react-router navigate function (useNavigate hook).
 * @return {{navigate: (to: To, options?: NavigateOptions) => void, navigateToBase: () => void | Promise<void>, brUuid: string, basePath: string, partnerUuid: string, navigateDelta: (delta: number) => void | Promise<void>}}
 */
export function useWealthPlanningNavigate() {
    const navigate = useNavigate();
    const { isWealthNavigatorMFE } = useYwuApp();
    const [searchParams] = useSearchParams();
    const routeParams = useParams<{ partnerUuid: string }>();

    const { pathname } = useLocation();

    const { businessRelationshipUuid, clientUuid, env, partnerUuid, isRerender, isOpenedInClientContext } = useMemo(() => {
        const splitPath = pathname.split('/');
        const getValidUUID = (uuid?: string | null) => (uuid && isValidUUID(uuid) ? uuid : '');

        const searchPartnerUuid = searchParams.get('partnerUuid');
        const isRerender = !!searchPartnerUuid;
        const isOpenedInClientContext = splitPath.some((path) => path === 'client-data');

        if (isWealthNavigatorMFE) {
            return isOpenedInClientContext
                ? {
                      clientUuid: splitPath[2],
                      businessRelationshipUuid: splitPath[5],
                      env: splitPath[3],
                      partnerUuid: getValidUUID(routeParams.partnerUuid || searchPartnerUuid || splitPath[6]),
                      isRerender,
                      isOpenedInClientContext,
                  }
                : {
                      partnerUuid: getValidUUID(routeParams.partnerUuid || searchPartnerUuid || splitPath[2]),
                      isRerender,
                  };
        } else {
            return { partnerUuid: getValidUUID(routeParams.partnerUuid || splitPath[2]) };
        }
    }, [pathname, isWealthNavigatorMFE, searchParams, routeParams.partnerUuid]);

    const basePath = useMemo(
        () => (businessRelationshipUuid ? `/client-data/${clientUuid}${env ? `/${env}` : ''}/wealth-planning/${businessRelationshipUuid}` : '/wealth-planning'),
        [businessRelationshipUuid, env, clientUuid],
    );
    const navigateWithBase = useCallback(
        (to: To, options?: NavigateOptions) => {
            if (typeof to === 'string') navigate(`${basePath}${to}`, options);
            else navigate({ pathname: `${basePath}${to.pathname}`, ...to }, options);
        },
        [basePath, navigate],
    );

    const navigateDelta = useCallback((delta: number) => navigate(delta.toString()), [navigate]);

    const navigateToBase = useCallback(() => navigate(basePath), [basePath, navigate]);

    return {
        clientUuid,
        businessRelationshipUuid,
        partnerUuid: isValidUUID(partnerUuid) ? partnerUuid : '',
        basePath,
        navigate: navigateWithBase,
        navigateDelta,
        navigateToBase,
        isRerender,
        isOpenedInClientContext,
    };
}
